/**
 * Shaping the telemetry response for charts (TRO-430). Pure, so the rules
 * that matter are tested without rendering anything:
 *
 *   - a pass-rate line per version, never one line across versions: a day
 *     with no runs under a version has no point for it (null), so a line
 *     never bridges a seam
 *   - states stack per day (counts, not rates, so adding versions is honest)
 *   - units and provider spend are separate series by attribution, never
 *     one number
 */

import { rebucket, bucketOf, hourKeyAt } from 'features/evals3/pages/timeBuckets';

export const ATTRIBUTIONS = ['live', 'backfill', 'audit'];

const days = (rows) => [...new Set(rows.map((r) => r.day))].sort();

/**
 * With `at` ({ size, buckets } from timeBuckets.clusterFor) the hourly rows
 * are regrouped into that size and laid out over every bucket, empty ones
 * included; without it each distinct `day` is a point, as it comes.
 */
const shape = (rows, at, groupBy) => (at ? { rows: rebucket(rows, at.size, groupBy), keys: at.buckets } : { rows, keys: days(rows) });

/** [{ day, v1: rate|null, v2: rate|null }] and the version keys, oldest first. */
export function passRateByVersion(series = [], at = null) {
  const versions = [...new Set(series.map((r) => r.version))].sort((a, b) => a - b);
  const { rows: grouped, keys } = shape(series, at, ['version']);
  const rows = keys.map((day) => {
    const row = { day };
    for (const v of versions) {
      const hit = grouped.find((r) => r.day === day && r.version === v);
      // Regrouped, the rate is recomputed from the counts — never an average of rates.
      const rate = !hit ? null : at ? ((hit.passed || 0) + (hit.failed || 0) ? hit.passed / (hit.passed + hit.failed) : null) : hit.pass_rate;
      row[`v${v}`] = rate !== null && rate !== undefined ? rate : null;
    }
    return row;
  });
  return { rows, keys: versions.map((v) => `v${v}`) };
}

/** [{ day, passed, failed, errored, pending, skipped }] — counts summed across versions. */
export function statesByDay(series = [], at = null) {
  const { rows: grouped, keys } = shape(series, at, []);
  return keys.map((day) => {
    const on = grouped.filter((r) => r.day === day);
    const sum = (k) => on.reduce((s, r) => s + (r[k] || 0), 0);
    return {
      day, passed: sum('passed'), failed: sum('failed'), errored: sum('errored'), pending: sum('pending'), skipped: sum('skipped'),
      // The eval's own score, averaged over the bucket's scored verdicts: a sum and a count, so regrouping stays honest.
      scored: sum('scored'), avg_score: sum('scored') ? sum('score_sum') / sum('scored') : null,
    };
  });
}

/** One field (units OR provider_cost_usd) per day, split by attribution. */
export function byAttribution(rows = [], field, at = null) {
  if (field !== 'units' && field !== 'provider_cost_usd') throw new Error('byAttribution field must be units or provider_cost_usd');
  const { rows: grouped, keys } = shape(rows, at, ['attribution']);
  return keys.map((day) => {
    const row = { day };
    for (const a of ATTRIBUTIONS) {
      const hit = grouped.find((r) => r.day === day && r.attribution === a);
      row[a] = hit ? hit[field] : 0;
    }
    return row;
  });
}

/** The bucket a seam falls in, on the viewer's clock, for chart reference lines. */
export function seamDays(seams = [], at = null, tz = 'UTC') {
  return seams.map((s) => ({
    version: s.version,
    day: at ? bucketOf(hourKeyAt(s.at, tz) || String(s.at).slice(0, 10), at.size) : String(s.at).slice(0, 10),
    change_note: s.change_note || '',
  }));
}

export function pct(x) {
  return x === null || x === undefined ? '—' : `${Math.round(x * 1000) / 10}%`;
}
